import type {
  SupabaseClient,
} from '@supabase/supabase-js';

import type {
  FamilyUnitType,
  RelationshipHistoryEntry,
  SpousePartnerEdge,
} from '@/lib/familyUnits';

import {
  createFailedCanonicalTreeExternalEvidenceLoadResult,
  createSuccessfulCanonicalTreeExternalEvidenceLoadResult,
  createUnavailableCanonicalTreeExternalEvidenceLoadResult,
  type CanonicalTreeRelationshipHistoryLoadResult,
  type CanonicalTreeSpousePartnerEdgeLoadResult,
} from './CanonicalTreeExternalEvidenceLoaderResult';

interface PostgrestLikeError {
  readonly code?: unknown;
  readonly message?: unknown;
  readonly details?: unknown;
  readonly hint?: unknown;
}

const stringOrNull = (
  value:
    unknown,
): string | null =>
  typeof value ===
    'string'
    ? value
    : null;

const errorFailure = (
  error:
    PostgrestLikeError,
) => ({
  code:
    stringOrNull(
      error.code,
    ),

  message:
    stringOrNull(
      error.message,
    ) ??
    'External evidence query failed.',

  details:
    stringOrNull(
      error.details,
    ) ??
    stringOrNull(
      error.hint,
    ),
});

const thrownFailure = (
  error:
    unknown,
) => ({
  code:
    null,

  message:
    error instanceof Error
      ? error.message
      : 'External evidence loader threw an unknown error.',

  details:
    error instanceof Error
      ? error.stack ?? null
      : null,
});

const unavailableErrorCodes =
  new Set([
    '42P01',
    '42703',
    'PGRST204',
    'PGRST205',
  ]);

const errorMeansUnavailable = (
  error:
    PostgrestLikeError,
): boolean => {
  const code =
    stringOrNull(
      error.code,
    );

  if (
    code &&
    unavailableErrorCodes.has(
      code,
    )
  ) {
    return true;
  }

  const message =
    (
      stringOrNull(
        error.message,
      ) ??
      ''
    ).toLowerCase();

  return (
    message.includes(
      'relation',
    ) &&
    message.includes(
      'does not exist',
    )
  ) ||
  message.includes(
    'could not find the table',
  ) ||
  message.includes(
    'could not find the column',
  ) ||
  message.includes(
    'schema cache',
  );
};

const coerceUnitType = (
  raw:
    unknown,
): FamilyUnitType => {
  if (
    typeof raw !==
    'string'
  ) {
    return 'married';
  }

  const value =
    raw
      .toLowerCase()
      .trim();

  switch (value) {
    case 'married':
    case 'partner':
    case 'fiance':
    case 'coparent':
    case 'divorced':
    case 'ex_spouse':
    case 'deceased_spouse':
    case 'step_parent':
    case 'in_law':
    case 'blended':
      return value;

    case 'fiancee':
    case 'fiancée':
    case 'engaged':
      return 'fiance';

    case 'spouse':
    case 'husband':
    case 'wife':
      return 'married';

    case 'ex':
    case 'ex-spouse':
    case 'exspouse':
      return 'ex_spouse';

    case 'co-parent':
    case 'co_parent':
      return 'coparent';

    case 'step-parent':
    case 'stepparent':
      return 'step_parent';

    case 'in-law':
    case 'inlaw':
      return 'in_law';

    default:
      return 'married';
  }
};

const mapSpousePartnerEdge = (
  row:
    Record<string, unknown>,
): SpousePartnerEdge => {
  const person1Id =
    String(
      row.person1_id ??
      '',
    );

  const person2Id =
    String(
      row.person2_id ??
      '',
    );

  const [
    personAId,
    personBId,
  ] =
    person1Id <
    person2Id
      ? [
          person1Id,
          person2Id,
        ]
      : [
          person2Id,
          person1Id,
        ];

  return {
    id:
      String(
        row.id ??
        '',
      ),

    ownerUserId:
      stringOrNull(
        row.owner_user_id,
      ),

    personAId,

    personBId,

    unitType:
      coerceUnitType(
        row.unit_type,
      ),

    relationshipStatus:
      stringOrNull(
        row.relationship_status,
      ),

    startedAt:
      stringOrNull(
        row.started_at,
      ),

    endedAt:
      stringOrNull(
        row.ended_at,
      ),

    isCurrent:
      typeof row.is_current ===
        'boolean'
        ? row.is_current
        : true,

    metadata:
      (
        row.metadata &&
        typeof row.metadata ===
          'object'
      )
        ? row.metadata as Record<string, unknown>
        : {},

    notes:
      stringOrNull(
        row.notes,
      ),

    createdAt:
      stringOrNull(
        row.created_at,
      ) ??
      '',
  };
};

const mapRelationshipHistory = (
  row:
    Record<string, unknown>,
): RelationshipHistoryEntry => ({
  id:
    String(
      row.id ??
      '',
    ),

  ownerUserId:
    String(
      row.owner_user_id ??
      '',
    ),

  personAId:
    String(
      row.person_a_id ??
      '',
    ),

  personBId:
    String(
      row.person_b_id ??
      '',
    ),

  relationshipKind:
    String(
      row.relationship_kind ??
      '',
    ),

  status:
    stringOrNull(
      row.status,
    ),

  startedAt:
    stringOrNull(
      row.started_at,
    ),

  endedAt:
    stringOrNull(
      row.ended_at,
    ),

  metadata:
    (
      row.metadata &&
      typeof row.metadata ===
        'object'
    )
      ? row.metadata as Record<string, unknown>
      : {},

  notes:
    stringOrNull(
      row.notes,
    ),

  createdAt:
    stringOrNull(
      row.created_at,
    ) ??
    '',
});

export const loadCanonicalTreeSpousePartnerEdgesExplicit = async (
  input: {
    readonly supabase:
      SupabaseClient;

    readonly ownerUserId:
      string;

    readonly now?:
      () => string;
  },
): Promise<CanonicalTreeSpousePartnerEdgeLoadResult> => {
  const now =
    input.now ??
    (() =>
      new Date()
        .toISOString());

  const requestedAt =
    now();

  try {
    const {
      data,
      error,
    } =
      await input.supabase
        .from(
          'spouse_partner_edges',
        )
        .select(
          '*',
        );

    const completedAt =
      now();

    if (error) {
      const failure =
        errorFailure(
          error,
        );

      return errorMeansUnavailable(
        error,
      )
        ? createUnavailableCanonicalTreeExternalEvidenceLoadResult({
            collection:
              'spouse_partner_edges',

            ownerUserId:
              input.ownerUserId,

            requestedAt,

            completedAt,

            failure,
          })
        : createFailedCanonicalTreeExternalEvidenceLoadResult({
            collection:
              'spouse_partner_edges',

            ownerUserId:
              input.ownerUserId,

            requestedAt,

            completedAt,

            failure,
          });
    }

    const rows =
      (
        data ??
        []
      )
        .map(
          row =>
            mapSpousePartnerEdge(
              row as Record<string, unknown>,
            ),
        )
        .filter(
          edge =>
            edge.ownerUserId ===
              input.ownerUserId ||
            edge.ownerUserId ===
              null,
        );

    return createSuccessfulCanonicalTreeExternalEvidenceLoadResult({
      collection:
        'spouse_partner_edges',

      ownerUserId:
        input.ownerUserId,

      rows,

      requestedAt,

      completedAt,
    });
  } catch (error) {
    return createFailedCanonicalTreeExternalEvidenceLoadResult({
      collection:
        'spouse_partner_edges',

      ownerUserId:
        input.ownerUserId,

      requestedAt,

      completedAt:
        now(),

      failure:
        thrownFailure(
          error,
        ),
    });
  }
};

export const loadCanonicalTreeRelationshipHistoryExplicit = async (
  input: {
    readonly supabase:
      SupabaseClient;

    readonly ownerUserId:
      string;

    readonly now?:
      () => string;
  },
): Promise<CanonicalTreeRelationshipHistoryLoadResult> => {
  const now =
    input.now ??
    (() =>
      new Date()
        .toISOString());

  const requestedAt =
    now();

  try {
    const {
      data,
      error,
    } =
      await input.supabase
        .from(
          'relationship_history',
        )
        .select(
          '*',
        )
        .eq(
          'owner_user_id',
          input.ownerUserId,
        );

    const completedAt =
      now();

    if (error) {
      const failure =
        errorFailure(
          error,
        );

      return errorMeansUnavailable(
        error,
      )
        ? createUnavailableCanonicalTreeExternalEvidenceLoadResult({
            collection:
              'relationship_history',

            ownerUserId:
              input.ownerUserId,

            requestedAt,

            completedAt,

            failure,
          })
        : createFailedCanonicalTreeExternalEvidenceLoadResult({
            collection:
              'relationship_history',

            ownerUserId:
              input.ownerUserId,

            requestedAt,

            completedAt,

            failure,
          });
    }

    const rows =
      (
        data ??
        []
      ).map(
        row =>
          mapRelationshipHistory(
            row as Record<string, unknown>,
          ),
      );

    return createSuccessfulCanonicalTreeExternalEvidenceLoadResult({
      collection:
        'relationship_history',

      ownerUserId:
        input.ownerUserId,

      rows,

      requestedAt,

      completedAt,
    });
  } catch (error) {
    return createFailedCanonicalTreeExternalEvidenceLoadResult({
      collection:
        'relationship_history',

      ownerUserId:
        input.ownerUserId,

      requestedAt,

      completedAt:
        now(),

      failure:
        thrownFailure(
          error,
        ),
    });
  }
};
